/**
 * Game State Adapter
 * Converts API response to game engine GameState format
 */

import { GameState, StageId, StageVariant } from '../models/GameState';
import { PlayerState, PlayerRole } from '../models/Player';
import { ItemId } from '../models/Inventory';
import { GameStateResponse } from '@/types/session';

/**
 * Convert API GameStateResponse to engine GameState format
 */
export function adaptGameStateResponse(response: GameStateResponse): GameState | null {
  if (!response.state) {
    return null;
  }

  const state = response.state;

  // Convert players
  const players: PlayerState[] = state.players.map(p => ({
    playerId: p.playerId,
    sessionId: response.sessionId,
    role: p.role as PlayerRole,
    name: p.name,
    scores: p.scores || {
      brain: 0,
      heart: 0,
      task: 0,
      communication: 0,
    },
    energy: p.energy,
    isAlive: p.isAlive,
    isPoisoned: p.isPoisoned,
    swimming: p.swimming || false,
    decisions: new Map(Object.entries(p.decisions || {})),
  }));

  // Convert NPCs
  const npcs = (state.npcs || []).map(npc => ({
    role: npc.role,
    energy: npc.energy,
    decisions: new Map(Object.entries(npc.decisions || {})),
  }));

  // Convert inventory (cast id to ItemId - API may return strings)
  const inventory = state.inventory.map(item => ({
    id: item.id as ItemId,
    name: item.name,
    quantity: item.quantity || 1,
  }));

  // Convert decisions
  const decisions = (state.decisions || []).map(d => ({
    questionId: d.questionId as any,
    playerDecisions: d.playerDecisions.map(pd => ({
      playerId: pd.playerId,
      questionId: pd.questionId as any,
      answer: pd.answer,
      timestamp: new Date(pd.timestamp),
    })),
    finalAnswer: d.finalAnswer,
    decisionMethod: d.decisionMethod as any,
    timestamp: new Date(d.timestamp),
  }));

  const gameState: GameState = {
    sessionId: response.sessionId,
    createdAt: new Date(),
    updatedAt: new Date(),
    currentStage: state.currentStage as StageId,
    currentStep: state.currentStep,
    currentVariant: state.currentVariant as StageVariant,
    huntingPermitDay: state.huntingPermitDay,
    players,
    npcs,
    teamName: state.teamName,
    teamEnergyPacks: state.teamEnergyPacks,
    inventory,
    decisions,
    flags: state.flags,
    gameStatus: state.gameStatus,
  };

  return gameState;
}

/**
 * Create a minimal GameState from session player data for when full state isn't available
 */
export function createMinimalGameState(
  sessionId: string,
  players: Array<{
    playerId: string;
    displayName: string;
    role?: string;
    energy: number;
  }>
): GameState {
  const playerStates: PlayerState[] = players.map(p => ({
    playerId: p.playerId,
    sessionId,
    role: (p.role || 'BIOLOGIST') as PlayerRole,
    name: p.displayName,
    scores: {
      brain: 0,
      heart: 0,
      task: 0,
      communication: 0,
    },
    energy: p.energy,
    isAlive: true,
    isPoisoned: false,
    swimming: false,
    decisions: new Map(),
  }));

  return {
    sessionId,
    createdAt: new Date(),
    updatedAt: new Date(),
    currentStage: 0,
    currentStep: '0.1',
    currentVariant: null,
    huntingPermitDay: 0,
    players: playerStates,
    npcs: [
      { role: 'DOC', energy: 100, decisions: new Map() },
      { role: 'GEE', energy: 100, decisions: new Map() },
    ],
    teamName: '',
    teamEnergyPacks: 37,
    inventory: [],
    decisions: [],
    flags: {
      stage5Variant: 'A',
      fuelLeakDiscovered: false,
      diverPoisoned: false,
      containerLost: false,
      rainbowRayCaptured: false,
      cleanupPartial: false,
      cleanupFull: false,
      goldenOctopusCaptured: false,
      missionComplete: false,
      allDead: false,
    },
    gameStatus: 'in_progress',
  };
}
